/**
 * PricingRuleScopeWidget — Scope, validity, and storefront settings.
 *
 * Shows:
 *   - Apply On (Item Code / Item Group / Brand / Transaction) — for non-coupon types
 *   - Quantity & Amount thresholds
 *   - Validity dates
 *   - Company
 *   - Storefront toggles (storefront_enabled, storefront_show_on_detail)
 *
 * Hidden until type is selected (progressive disclosure).
 * Follows StockEntryWarehouseWidget pattern.
 */

import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";
import { CARD } from "@/config/design-tokens";

const i18n: Record<string, { en: string; th: string }> = {
  apply_on: { en: "Apply On", th: "ใช้กับ" },
  item_code: { en: "Item Code", th: "รหัสสินค้า" },
  item_group: { en: "Item Group", th: "กลุ่มสินค้า" },
  brand: { en: "Brand", th: "แบรนด์" },
  transaction: { en: "Transaction (all items)", th: "ทั้งบิล (ทุกสินค้า)" },
  select_item: { en: "Select item...", th: "เลือกสินค้า..." },
  select_group: { en: "Select group...", th: "เลือกกลุ่ม..." },
  select_brand: { en: "Select brand...", th: "เลือกแบรนด์..." },
  min_qty: { en: "Min Qty", th: "จำนวนขั้นต่ำ" },
  max_qty: { en: "Max Qty", th: "จำนวนสูงสุด" },
  min_amt: { en: "Min Amount", th: "ยอดขั้นต่ำ" },
  max_amt: { en: "Max Amount", th: "ยอดสูงสุด" },
  valid_from: { en: "Valid From", th: "เริ่มใช้ได้" },
  valid_upto: { en: "Valid Until", th: "ใช้ได้ถึง" },
  company: { en: "Company", th: "บริษัท" },
  select_company: { en: "Select company...", th: "เลือกบริษัท..." },
  storefront_checkout: { en: "Enable for Storefront Checkout", th: "เปิดใช้ในหน้าชำระเงิน" },
  storefront_checkout_hint: {
    en: "Apply this rule when customers check out on the storefront",
    th: "ใช้กฎนี้เมื่อลูกค้าชำระเงินบนหน้าร้าน",
  },
  storefront_detail: { en: "Show on Storefront Detail", th: "แสดงในหน้ารายละเอียดสินค้า" },
  storefront_detail_hint: {
    en: "Display this promotion on the product detail page",
    th: "แสดงโปรโมชั่นนี้ในหน้ารายละเอียดสินค้า",
  },
};

export function PricingRuleScopeWidget({
  formData,
  onFieldChange,
  disabled,
  isSaving,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const discountType = formData.price_or_product_discount;
  const isCoupon = !!formData.coupon_code_based;
  const isInputDisabled = disabled || isSaving;

  // Don't render until type is selected
  if (!discountType) return null;

  const applyOn = (formData.apply_on as string) || "";

  // Coupon type auto-sets apply_on=Transaction, so hide the selector
  const showApplyOnSelector = !isCoupon;

  return (
    <div>
      <SectionHeader
        label={getSectionTitleTranslation("Scope & Validity", language)}
      />
      <div className={cn(CARD, "p-6 space-y-6")}>
        {/* Apply On selector (hidden for coupons — always Transaction) */}
        {showApplyOnSelector && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{t("apply_on")}</Label>
              <Select
                value={applyOn}
                onValueChange={(val) => onFieldChange("apply_on", val)}
                disabled={isInputDisabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("apply_on")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Item Code">{t("item_code")}</SelectItem>
                  <SelectItem value="Item Group">{t("item_group")}</SelectItem>
                  <SelectItem value="Brand">{t("brand")}</SelectItem>
                  <SelectItem value="Transaction">{t("transaction")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Conditional link field based on apply_on */}
            {applyOn === "Item Code" && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("item_code")}</Label>
                <DocTypeSearchAutocomplete
                  doctype="Item"
                  value={(formData.item_code as string) || ""}
                  onChange={(val) => onFieldChange("item_code", val)}
                  placeholder={t("select_item")}
                  disabled={isInputDisabled}
                  showInitialSuggestions
                  minSearchLength={0}
                />
              </div>
            )}
            {applyOn === "Item Group" && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("item_group")}</Label>
                <DocTypeSearchAutocomplete
                  doctype="Item Group"
                  value={(formData.item_group as string) || ""}
                  onChange={(val) => onFieldChange("item_group", val)}
                  placeholder={t("select_group")}
                  disabled={isInputDisabled}
                  showInitialSuggestions
                  minSearchLength={0}
                />
              </div>
            )}
            {applyOn === "Brand" && (
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">{t("brand")}</Label>
                <DocTypeSearchAutocomplete
                  doctype="Brand"
                  value={(formData.brand as string) || ""}
                  onChange={(val) => onFieldChange("brand", val)}
                  placeholder={t("select_brand")}
                  disabled={isInputDisabled}
                  showInitialSuggestions
                  minSearchLength={0}
                />
              </div>
            )}
          </div>
        )}

        {/* Quantity & Amount thresholds */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {language === "th" ? "เงื่อนไข" : "Conditions"}
          </Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{t("min_qty")}</Label>
              <Input
                type="number"
                value={(formData.min_qty as number) ?? ""}
                onChange={(e) => onFieldChange("min_qty", parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={isInputDisabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{t("max_qty")}</Label>
              <Input
                type="number"
                value={(formData.max_qty as number) ?? ""}
                onChange={(e) => onFieldChange("max_qty", parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={isInputDisabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{t("min_amt")}</Label>
              <Input
                type="number"
                value={(formData.min_amt as number) ?? ""}
                onChange={(e) => onFieldChange("min_amt", parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={isInputDisabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{t("max_amt")}</Label>
              <Input
                type="number"
                value={(formData.max_amt as number) ?? ""}
                onChange={(e) => onFieldChange("max_amt", parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={isInputDisabled}
              />
            </div>
          </div>
        </div>

        {/* Validity dates */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {language === "th" ? "ระยะเวลา" : "Validity"}
          </Label>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{t("valid_from")}</Label>
              <Input
                type="date"
                value={(formData.valid_from as string) || ""}
                onChange={(e) => onFieldChange("valid_from", e.target.value)}
                disabled={isInputDisabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">{t("valid_upto")}</Label>
              <Input
                type="date"
                value={(formData.valid_upto as string) || ""}
                onChange={(e) => onFieldChange("valid_upto", e.target.value)}
                disabled={isInputDisabled}
              />
            </div>
          </div>
        </div>

        {/* Company */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">{t("company")}</Label>
          <DocTypeSearchAutocomplete
            doctype="Company"
            value={(formData.company as string) || ""}
            onChange={(val) => onFieldChange("company", val)}
            placeholder={t("select_company")}
            disabled={isInputDisabled}
            showInitialSuggestions
            minSearchLength={0}
          />
        </div>

        {/* Storefront toggles */}
        <div className="space-y-4 pt-2 border-t">
          <Label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {language === "th" ? "หน้าร้าน" : "Storefront"}
          </Label>
          <div className="flex items-start gap-3">
            <Checkbox
              checked={!!formData.storefront_enabled}
              onCheckedChange={(checked) =>
                onFieldChange("storefront_enabled", checked ? 1 : 0)
              }
              disabled={isInputDisabled}
              id="storefront_enabled"
            />
            <div>
              <Label htmlFor="storefront_enabled" className="text-sm font-medium cursor-pointer">
                {t("storefront_checkout")}
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("storefront_checkout_hint")}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Checkbox
              checked={!!formData.storefront_show_on_detail}
              onCheckedChange={(checked) =>
                onFieldChange("storefront_show_on_detail", checked ? 1 : 0)
              }
              disabled={isInputDisabled}
              id="storefront_show_on_detail"
            />
            <div>
              <Label
                htmlFor="storefront_show_on_detail"
                className="text-sm font-medium cursor-pointer"
              >
                {t("storefront_detail")}
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("storefront_detail_hint")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
